import {type Descendant, Text as SlateText} from 'slate';

import type {ContentPart} from '../../../agentChat/store';

/**
 * slateToBodyParts converts the composer's Slate document into the message body
 * parts (text plus inline run mentions). The body leads with an empty text part
 * so a leading inline mention renders correctly and files can anchor after it.
 */
export function slateToBodyParts(nodes: Descendant[]): ContentPart[] {
  const parts: ContentPart[] = [{type: 'text', text: '', status: 'complete'}];

  walkSlate(nodes, chunk => {
    if (chunk.kind === 'text' && chunk.text.length > 0) {
      const last = parts[parts.length - 1];
      if (last?.type === 'text') {
        last.text += chunk.text;
      } else {
        parts.push({type: 'text', text: chunk.text, status: 'complete'});
      }
    } else if (chunk.kind === 'run-mention') {
      parts.push({
        type: 'wandb-run',
        // Capture entity/project from when the run was added so it stays
        // accurate if the user navigates away before sending.
        entityName: chunk.entityName,
        projectName: chunk.projectName,
        runName: chunk.name,
        runDisplayName: chunk.displayName,
        runDisplayNameStatus: 'resolved',
      });
    }
  });

  // Strip the space Slate auto-appends after a run mention, and the leading
  // delimiter itself when the body has no text.
  const last = parts[parts.length - 1];
  if (last?.type === 'text' && last.text.trim() === '') {
    parts.pop();
  }

  return parts;
}

type SlateChunk =
  | {kind: 'text'; text: string}
  | {
      kind: 'run-mention';
      name: string;
      displayName: string;
      entityName: string;
      projectName: string;
    };

function walkSlate(
  nodes: Descendant[],
  emit: (chunk: SlateChunk) => void
): void {
  nodes.forEach((node, index) => {
    if (index > 0) {
      emit({kind: 'text', text: '\n'});
    }

    walkSlateInline(node, emit);
  });
}

function walkSlateInline(
  node: Descendant,
  emit: (chunk: SlateChunk) => void
): void {
  if (SlateText.isText(node)) {
    if (node.text.length > 0) {
      emit({kind: 'text', text: node.text});
    }

    return;
  }

  if (node.type === 'run-mention') {
    emit({
      kind: 'run-mention',
      name: node.name,
      displayName: node.displayName,
      entityName: node.entityName,
      projectName: node.projectName,
    });

    return;
  }

  if (node.children) {
    for (const child of node.children) {
      walkSlateInline(child, emit);
    }
  }
}
